import React, { useMemo } from 'react';
import classnames from 'classnames/bind';
import { keys, noop } from 'lodash';
import { Tab, TabContainer, TabList, TabPanel } from 'tuui';
import { FileTypes, HelpTooltip } from 'taboola-ultimate-ui';
import { BetaTag } from 'components';
import { useAvailableItems } from 'hooks';
import EMPTY_FIELD_VALUE from 'modules/campaigns/modules/campaigns-reports/config/defaultValues';
import { TooltipSection } from 'modules/campaigns/modules/common-campaign-form';
import CONTENT_TYPES from 'modules/campaigns/modules/common-campaign-form/config/contentTypes';
import { ONLY_IMAGE_FILES } from 'modules/campaigns/modules/creative-creator/config/fileTypeConfigs';
import { MediaTabsContextProvider } from 'modules/campaigns/modules/creative-editor/components/MediaTabs';
import { CREATIVE_EDITOR_TABS } from 'modules/campaigns/modules/creative-editor/config';
import { thumbnailTabItems } from 'modules/campaigns/modules/creative-editor/config/thumbnailTabItems';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import styles from './mediaPicker.module.scss';

const classNameBuilder = classnames.bind(styles);

export const getContentTypeByFileTypes = fileTypes => {
    const hasImage = keys(fileTypes).includes(FileTypes.IMAGE);
    const hasMotion = keys(fileTypes).includes(FileTypes.VIDEO);

    if (hasImage) {
        if (hasMotion) {
            return;
        }
        return CONTENT_TYPES.PHOTO;
    }

    return CONTENT_TYPES.VIDEO;
};

export const MediaPicker = ({
    children,
    fileTypes = ONLY_IMAGE_FILES,
    value,
    onSelectMedia,
    onCancel,
    disabled,
    tabsConditionData,
    tabsContext,
    tabsDisabled,
    openCropModal = noop,
}) => {
    const { campaignId } = tabsContext;
    const contentType = getContentTypeByFileTypes(fileTypes);
    const creativeType = tabsConditionData?.creativeType;
    const tabItems = useMemo(
        () =>
            thumbnailTabItems.map(({ id, caption, component, ...rest }) => ({
                ...rest,
                id,
                caption: {
                    id:
                        id === CREATIVE_EDITOR_TABS.MEDIA_LIBRARY
                            ? caption.id
                            : `creative.editor.tab.${id}.caption.${creativeType || EMPTY_FIELD_VALUE}`,
                    default: caption.default,
                },
                Component: component,
            })),
        [creativeType]
    );

    const availableItems = useAvailableItems(tabItems, tabsConditionData);

    const tabHeaders = availableItems.map(
        ({ caption, id, metricsAttributes, isBeta = false, tagMessage, tooltip, icon }) => (
            <Tab
                key={id}
                disabled={tabsDisabled || disabled}
                {...metricsAttributes({ campaignId })}
                className={styles['tab-headers']}
            >
                {icon}
                <FormattedMessage id={caption.id} defaultMessage={caption.defaultMessage} />
                {tooltip ? (
                    <HelpTooltip iconClassName={styles['tooltip']}>
                        <TooltipSection>
                            <FormattedMessage {...tooltip} />
                        </TooltipSection>
                    </HelpTooltip>
                ) : null}
                {isBeta ? (
                    <BetaTag
                        containerClassName={styles['beta-tag']}
                        tagMessageClassName={styles['beta-tag-text']}
                        tagMessage={tagMessage}
                        tooltipMessage=""
                    />
                ) : null}
            </Tab>
        )
    );

    const tabContent = availableItems.map(({ Component, id }) => (
        <TabPanel key={id} className={styles['content-container']}>
            <Component
                value={value}
                onSelectMedia={onSelectMedia}
                uploadButtonFileTypes={fileTypes}
                contentType={contentType}
                onCancel={onCancel}
                disabled={disabled}
            />
        </TabPanel>
    ));
    return (
        <div>
            {children}
            <MediaTabsContextProvider openCropModal={openCropModal} {...tabsContext}>
                <TabContainer className={classNameBuilder('input-tabs', { disabled: tabsDisabled || disabled })}>
                    <TabList>{tabHeaders}</TabList>
                    {tabContent}
                </TabContainer>
            </MediaTabsContextProvider>
        </div>
    );
};

MediaPicker.defaultProps = {
    onSelectMedia: noop,
    tabsContext: {
        thumbnails: [],
    },
    tabsConditionData: {},
};
